import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Container, Form, Icon, Input, Message, Modal, TabPane } from 'semantic-ui-react';
import Tab from '~/components/Tab';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import externalAxios from 'axios';

import { getCompany, patchCompany, requestCompanyLogoUpload, updateCompanyName } from '~/api/company';
import { CompanyProjectVisibilityForm } from '~/features/user/settings/CompanySettings/CompanyProjectVisibilityForm';
import { CompanySubtypeTagsForm } from '~/features/user/settings/CompanySettings/CompanySubtypeTagsForm';
import { usePermissions } from '~/requests/permissions/usePermissions';
import BuyerProfileTab from '~/features/buyerProfile/BuyerProfileTab';
import InvestorProfile from './InvestorProfile';
import CompanyProfileAdminTab from './CompanyProfileAdminTab';
import CompanyProfileTeamTab from './CompanyProfileTeamTab';

import './styles.scss';

const CompanyProfile: React.FC = () => {

    const [company, setCompany] = useState<any>(null);
    const [editingCompanyName, setEditingCompanyName] = useState(false);
    const [draftCompanyName, setDraftCompanyName] = useState('');
    const [savingCompanyName, setSavingCompanyName] = useState(false);
    const [draftWebsite, setDraftWebsite] = useState('');
    const [savingWebsite, setSavingWebsite] = useState(false);
    const [logoModalOpen, setLogoModalOpen] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const { id: companyId } = useParams<{ id: string }>();
    const history = useHistory();
    const location = useLocation();
    const { permissions, status: permissionsStatus } = usePermissions();

    const onLogoDrop = useCallback(
        async (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];
            if (!file || !companyId) {
                return;
            }
            setUploadingLogo(true);
            try {
                const data = await requestCompanyLogoUpload(companyId, file.name);
                if (data.logoUploadPresignedUrl) {
                    await externalAxios.put(data.logoUploadPresignedUrl, file, {
                        headers: { 'Content-Type': file.type || 'application/octet-stream' },
                    });
                }
                const fresh = await getCompany(companyId);
                setCompany(fresh);
                toast.success('Company logo updated.');
                setLogoModalOpen(false);
            } catch (err) {
                console.error(err);
                toast.error('Failed to update company logo.');
            } finally {
                setUploadingLogo(false);
            }
        },
        [companyId]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: 'image/jpeg,image/png,image/svg+xml,image/webp,image/gif',
        maxFiles: 1,
        disabled: !logoModalOpen || uploadingLogo,
        noKeyboard: true,
        onDrop: files => {
            void onLogoDrop(files);
        },
    });

    const saveCompanyName = useCallback(async () => {
        if (!companyId || !company) {
            return;
        }
        const trimmed = draftCompanyName.trim();
        if (!trimmed) {
            toast.error('Company name cannot be empty.');
            return;
        }
        if (trimmed === company.name) {
            setEditingCompanyName(false);
            return;
        }
        setSavingCompanyName(true);
        try {
            await updateCompanyName(companyId, trimmed);
            setCompany((prev: any) => (prev ? { ...prev, name: trimmed } : prev));
            toast.success('Company name updated.');
            setEditingCompanyName(false);
        } catch (err) {
            console.error(err);
            toast.error('Failed to update company name.');
        } finally {
            setSavingCompanyName(false);
        }
    }, [companyId, company, draftCompanyName]);

    const cancelEditCompanyName = useCallback(() => {
        if (company) {
            setDraftCompanyName(company.name);
        }
        setEditingCompanyName(false);
    }, [company]);

    const saveCompanyWebsite = useCallback(async () => {
        if (!companyId || !company) {
            return;
        }
        const trimmed = draftWebsite.trim();
        const current = (company.website ?? '').trim();
        if (trimmed === current) {
            return;
        }
        setSavingWebsite(true);
        try {
            await patchCompany(companyId, { website: trimmed || null });
            const fresh = await getCompany(companyId);
            setCompany(fresh);
            setDraftWebsite(fresh.website ?? '');
            toast.success('Company website updated.');
        } catch (err) {
            console.error(err);
            toast.error('Failed to update company website.');
        } finally {
            setSavingWebsite(false);
        }
    }, [companyId, company, draftWebsite]);

    ReactGA.send({
        hitType: 'pageview',
        page: '/company-profile/id',
        title: `${company?.name} | Conductor Solar`
    });
    document.title = `${company?.name} | Conductor Solar`;

    useEffect(() => {
        if (companyId) {
            getCompany(companyId).then(data => setCompany(data));
        }
    }, [companyId]);

    useEffect(() => {
        if (company) {
            document.title = `${company.name} - Company Profile`;
        }
    }, [company]);

    useEffect(() => {
        setDraftWebsite(company?.website ?? '');
    }, [company?.website]);

    const companyTypeLabel = useMemo(() => {
        if (!company?.type) {
            return null;
        }
        if (Array.isArray(company.type)) {
            return company.type.join(', ');
        }
        return String(company.type);
    }, [company?.type]);

    const companySubtypeLabel = useMemo(() => {
        const subtypes = company?.subtypeCodes;
        if (!Array.isArray(subtypes) || subtypes.length === 0) {
            return null;
        }
        return subtypes.join(', ');
    }, [company?.subtypeCodes]);

    const accessStatusLabel = useMemo(() => {
        return company?.active === false ? 'Deactivated' : 'Active';
    }, [company?.active]);

    const getTabKey = useCallback((label: string) => label.toLowerCase().replace(/\s+/g, '-'), []);

    const tabPanes = useMemo(() => {
        if (!company) {
            return [];
        }
        const panes = [];
        const isInvestorCompany = !!company.type && company.type.includes('investor');
        if (isInvestorCompany) {
            panes.push({
                menuItem: 'Buyer Profile',
                render: () => (
                    <TabPane attached={false} className='buyer-profile-tab-pane'>
                        <BuyerProfileTab companyId={companyId!} isAdmin />
                    </TabPane>
                ),
            });
        }
        if (permissions?.isAdmin) {
            panes.push({
                menuItem: 'Team',
                render: () => (
                    <TabPane attached={false}>
                        <CompanyProfileTeamTab company={company} />
                    </TabPane>
                ),
            });
        }
        if (permissions?.canChangeCompanySettings || permissions?.isAdmin) {
            panes.push({
                menuItem: 'Settings',
                render: () => (
                    <TabPane attached={false}>
                        <div className='company-profile-settings-tab'>
                            <div className='company-profile-settings-section company-profile-settings-website'>
                                <Form.Field>
                                    <label>Company website</label>
                                    <div className='company-profile-settings-website-row'>
                                        <Input
                                            fluid
                                            placeholder='https://example.com'
                                            value={draftWebsite}
                                            disabled={savingWebsite}
                                            onChange={(_e, data) => setDraftWebsite(String(data.value ?? ''))}
                                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    void saveCompanyWebsite();
                                                }
                                            }}
                                        />
                                        <Button
                                            type='button'
                                            primary
                                            size='small'
                                            loading={savingWebsite}
                                            disabled={savingWebsite}
                                            onClick={() => void saveCompanyWebsite()}
                                            content='Save'
                                        />
                                    </div>
                                </Form.Field>
                            </div>
                            <div className='company-profile-settings-section'>
                                <CompanySubtypeTagsForm
                                    source='companyProfile'
                                    companyId={companyId!}
                                    subtypeCodes={company.subtypeCodes ?? []}
                                    onCompanyUpdated={setCompany}
                                />
                            </div>
                            <div className='company-profile-settings-section'>
                                <CompanyProjectVisibilityForm
                                    source='companyProfile'
                                    companyId={companyId!}
                                    projectVisibility={company.projectVisibility ?? 'ALL'}
                                    onCompanyUpdated={setCompany}
                                />
                            </div>

                            {permissions?.isAdmin && (
                                <div className='company-profile-settings-section company-profile-settings-section--admin'>
                                    <CompanyProfileAdminTab
                                        company={company}
                                        onCompanyUpdated={setCompany}
                                    />
                                </div>
                            )}
                        </div>
                    </TabPane>
                ),
            });
        }
        if (isInvestorCompany) {
            panes.push({
                menuItem: 'Investor Profile (legacy)',
                render: () => (
                    <TabPane attached={false}>
                        <InvestorProfile />
                    </TabPane>
                ),
            });
        }
        return panes;
    }, [
        company,
        companyId,
        permissions?.isAdmin,
        permissions?.canChangeCompanySettings,
        draftWebsite,
        savingWebsite,
        saveCompanyWebsite,
    ]);

    const tabKeys = useMemo(
        () => tabPanes.map((pane: any) => getTabKey(String(pane.menuItem))),
        [tabPanes, getTabKey]
    );

    const activeTabIndex = useMemo(() => {
        if (tabKeys.length === 0) {
            return 0;
        }
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        if (!tabParam) {
            return 0;
        }
        const idx = tabKeys.findIndex((key) => key === tabParam);
        return idx >= 0 ? idx : 0;
    }, [location.search, tabKeys]);

    const onTabChange = useCallback(
        (_ignored: React.SyntheticEvent, data: { activeIndex?: string | number }) => {
            const rawIndex = data.activeIndex;
            const nextIndex =
                typeof rawIndex === 'number'
                    ? rawIndex
                    : typeof rawIndex === 'string'
                        ? Number(rawIndex) || 0
                        : 0;
            const nextKey = tabKeys[nextIndex];
            if (!nextKey) {
                return;
            }
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('tab', nextKey);
            history.replace(`${location.pathname}?${searchParams.toString()}`);
        },
        [history, location.pathname, location.search, tabKeys]
    );

    useEffect(() => {
        if (company && company.website && !company.website.startsWith('https://')) {
            setCompany({ ...company, website: `https://${company.website}` });
        }
    }, [company]);

    if (permissionsStatus === 'loading') {
        return null;
    }
    if (!permissions?.isAdmin) {
        return <Message negative>Only admin users can view company profiles.</Message>;
    }

    return (

        <div className="company-profile-page">
            <Container>
                {company ? (
                    <div className="company-profile">
                        <div
                            className={`company-profile-header card${company.active === false ? ' company-profile-header--inactive' : ''}`}
                        >
                            <div className='company-profile-card-heading'>Company details</div>
                            <div className='company-profile-header-body'>
                                {permissions?.isAdmin ? (
                                    <div className='company-logo-wrap'>
                                        <img
                                            src={company.logoS3Url}
                                            alt={`${company.name} logo`}
                                            className='company-logo'
                                        />
                                        <button
                                            type='button'
                                            className='company-logo-edit-btn'
                                            aria-label='Change company logo'
                                            onClick={() => setLogoModalOpen(true)}
                                        >
                                            <Icon name='pencil' />
                                        </button>
                                    </div>
                                ) : (
                                    <img
                                        src={company.logoS3Url}
                                        alt={`${company.name} logo`}
                                        className='company-logo'
                                    />
                                )}
                                <div className='company-profile-header-main'>
                                    {permissions?.isAdmin && editingCompanyName ? (
                                        <div className='company-profile-name-edit'>
                                            <div className='company-profile-name-edit-input-wrap'>
                                                <Input
                                                    fluid
                                                    size='large'
                                                    value={draftCompanyName}
                                                    disabled={savingCompanyName}
                                                    autoFocus
                                                    onChange={(_e, data) => setDraftCompanyName(String(data.value ?? ''))}
                                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            void saveCompanyName();
                                                        }
                                                        if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            cancelEditCompanyName();
                                                        }
                                                    }}
                                                    aria-label='Company name'
                                                />
                                            </div>
                                            <div className='company-profile-name-edit-actions'>
                                                <Button
                                                    type='button'
                                                    positive
                                                    icon='check'
                                                    size='small'
                                                    loading={savingCompanyName}
                                                    disabled={savingCompanyName}
                                                    onClick={() => void saveCompanyName()}
                                                    aria-label='Save company name'
                                                />
                                                <Button
                                                    type='button'
                                                    basic
                                                    icon='close'
                                                    size='small'
                                                    disabled={savingCompanyName}
                                                    onClick={cancelEditCompanyName}
                                                    aria-label='Cancel editing company name'
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='company-profile-name-row'>
                                            <h1>{company.name}</h1>
                                            {permissions?.isAdmin && (
                                                <Button
                                                    type='button'
                                                    size='tiny'
                                                    className='company-profile-edit-name-btn'
                                                    aria-label='Edit company name'
                                                    style={{ padding: '8px 6px' }}
                                                    onClick={() => {
                                                        setDraftCompanyName(company.name);
                                                        setEditingCompanyName(true);
                                                    }}
                                                >
                                                    <Icon name='pencil' />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                    <div className='company-profile-data-bar'>
                                        <div className='data-item'>
                                            <Icon name='globe' style={{ marginTop: '-8px' }} />
                                            {company.website ? (
                                                <a
                                                    href={company.website}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='data-item data-item--link'
                                                >
                                                    {company.website}
                                                </a>
                                            ) : (
                                                <span>(No website provided)</span>
                                            )}
                                        </div>
                                        {companyTypeLabel || companySubtypeLabel ? (
                                            <div className='data-item'>
                                                <Icon name='building outline' style={{ marginTop: '-8px' }} />
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {companyTypeLabel ?? 'Company'}
                                                    {companySubtypeLabel ? ` (${companySubtypeLabel})` : ''}
                                                </span>
                                            </div>
                                        ) : null}
                                        {accessStatusLabel ? (
                                            <div className='data-item'>
                                                <Icon name='key' style={{ marginTop: '-8px' }} />
                                                <span>Status: {accessStatusLabel}</span>
                                            </div>
                                        ) : null}
                                    </div>

                                </div>
                            </div>
                        </div>
                        {tabPanes.length > 0 ? (
                            <div className="company-profile-tabs">
                                <Tab
                                    menu={{ secondary: true, pointing: true }}
                                    panes={tabPanes}
                                    activeIndex={activeTabIndex}
                                    onTabChange={onTabChange}
                                />
                            </div>
                        ) : null}
                        {permissions?.isAdmin && (
                            <Modal
                                size='tiny'
                                open={logoModalOpen}
                                onClose={() => {
                                    if (!uploadingLogo) {
                                        setLogoModalOpen(false);
                                    }
                                }}
                            >
                                <Modal.Header>Update company logo</Modal.Header>
                                <Modal.Content>
                                    <p className='company-profile-logo-modal-hint'>
                                        PNG or JPG format recommended. Use a square image, ideally 512x512 px (minimum 256x256 px), for best results.
                                    </p>
                                    <div
                                        {...getRootProps()}
                                        className={`company-profile-logo-dropzone${isDragActive ? ' company-profile-logo-dropzone--active' : ''
                                            }`}
                                    >
                                        <input {...getInputProps()} />
                                        <Icon name='image outline' size='large' />
                                        <p>
                                            {uploadingLogo
                                                ? 'Uploading…'
                                                : isDragActive
                                                    ? 'Drop the image here'
                                                    : 'Drag an image here, or click to browse'}
                                        </p>
                                    </div>
                                </Modal.Content>
                            </Modal>
                        )}
                    </div>
                ) : (
                    <p>Loading company data...</p>
                )}
            </Container>
        </div>
    );
};

export default CompanyProfile;